/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn (ví dụ: import { AppService } from '@core/services/app.service').
 * ------------------------------------------------------------------------------------------------------------
 */
import { Inject, Injectable, PLATFORM_ID, ViewContainerRef } from "@angular/core";
import { BehaviorSubject, map, ReplaySubject, Subject } from "rxjs";
import { Title } from "@angular/platform-browser";
import { TranslocoService } from "@jsverse/transloco";
import { LocalStorageService } from "@core/services/storage.service";
import { Ibreadcrumb, IProgressBar, ISubEvents } from "@core/models";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "@environments/environment";
import { IBusiness, IBusinessOption } from '@shared/models/business';
// CHÚ Ý KIẾN TRÚC: Luôn import trực tiếp từ file nguồn, KHÔNG import từ index barrel của authentication 
// để tránh lỗi Circular Dependency (NG0200) trong môi trường SSR/Production.
import { IUserDataLogin } from '../authentication/auth.model';
import { castInt, isArray } from '@shared/utils';
import { filter, mergeMap } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { STORAGE_KEY } from "../constants";


interface RouteData {
  title?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AppService {
  // Dùng tình huống muốn gửi 1 sự kiện riêng theo mục đính cụ thể tùy biến
  // ISubEvents: Mô tả cụ thể cấu trúc hỗ trợ
  subjectEvents$: Subject<ISubEvents> = new Subject<ISubEvents>();

  // Click double menu
  previousUrl: any = null;
  currentUrl: any = this.getLocationPathName();
  menuClickSubject$ = new Subject<string>();

  // Thông báo thay đổi doanh nghiệp

  // Thay đổi tên trang: đang chỉ dùng cho core các màn không cần xử lý riêng
  subjectTitle = new ReplaySubject<string>(1)

  processSub = new BehaviorSubject<IProgressBar>({ show: false, progress: 0 });
  loadingSub = new Subject<boolean>();
  spinnerSub = new Subject<boolean>();

  viewRootContainerRef: any = null; // Ref global handler custom
  editorInstance?: any;
  screenWidth!: number;

  private isDeployCodeNew = false; // Đánh dấu đã có phiên bản code mới hơn vừa được build và deploy
  isAdmin: boolean = false; // Đánh dấu user có phải admin không


  private dataTemp: { [k: string]: any } = {} // lưu các data dùng chung cho 1 lần f5

  breadcrumbs: Ibreadcrumb[] = [];
  private _businessId = 0;

  isOpenNavMobile = false;

  // đang để false cho convert được và test xong, sẽ đổi thành true cho hỗ trợ cả link cũ
  isSupportOldDateFormat = true // có hỗ trợ format date từ bản cũ khi copy link vd: fromDate=01/10/2024&toDate=30/10/2024

  private _userData!: IUserDataLogin;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    public store: LocalStorageService,
    private titleService: Title,
    private _transLoco: TranslocoService,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
  ) {

    this.onSetTitlePage()
    if (isPlatformBrowser(this.platformId)) {
      this.screenWidth = window.innerWidth;
      window.addEventListener('resize', () => {
        this.screenWidth = window.innerWidth;
      });
    }
  }

  /**
   * hàm set lại title tự bắt theo tile đã khai báo trong route
   */
  onSetTitlePage() {
    this._router.events
      .pipe(filter(e => e instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap(route => route.data))
      .subscribe((d: RouteData) => {
        if (d.title) {
          this.setPageTitle(this.translate(d.title));
        }
      });
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * DATA COMMON  ------------------------------------------------------------------------------------------------------
   * -------------------------------------------------------------------------------------------------------------------
   * */
  getLocationPathName() {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.pathname
    }
    return '';
  }



  /**
   * -------------------------------------------------------------------------------------------------------------------
   * EVENTS ------------------------------------------------------------------------------------------------------------
   * -------------------------------------------------------------------------------------------------------------------
   * */
  /**
   * Handler loading
   * */
  showLoading = () => setTimeout(() => this.loadingSub.next(true))
  hideLoading = () => setTimeout(() => this.loadingSub.next(false))


  /**
   * Handler loading spinner
   * */
  showSpinner = () => setTimeout(() => this.spinnerSub.next(true))
  hideSpinner = () => setTimeout(() => this.spinnerSub.next(false))
  showProgressBar = () => this.processSub.next({ progress: 0, show: true });

  updateProgressBar(current: number, total: number) {
    const progress = ((current / total) * 100).toFixed(2);
    this.processSub.next({ progress: parseFloat(progress), show: true });
    if (current == total) {
      setTimeout(() => {
        this.hideProgressBar();
      }, 1000)
    }
  }

  hideProgressBar = () => this.processSub.next({ progress: 0, show: false });


  /**
   * -------------------------------------------------------------------------------------------------------------------
   * Language ----------------------------------------------------------------------------------------------------------
   * -------------------------------------------------------------------------------------------------------------------
   * */
  get trans() {
    return this._transLoco;
  }

  translate(key: string | any) {
    if (!key) {
      return '';
    }
    return this._transLoco.translate(key);
  }

  setPageTitle(key: string, suffix: number | string = '', disableTranslate = false) {
    if (key) {
      if (disableTranslate) {
        this.titleService.setTitle(`${key} ${suffix}`)
      } else {
        this.subjectTitle.next(key);
        this.trans.selectTranslate(`${key}`).subscribe({
          next: v => v && this.titleService.setTitle(`${v} ${suffix}`)
        });
      }
    }
  }

  setViewRootContainerRef = (ref: ViewContainerRef) => {
    this.viewRootContainerRef = ref;
  }

  getViewRootContainerRef = () => this.viewRootContainerRef;

  get getUserId(): number {
    if (!this._userData) return 0;

    return this._userData.id ? castInt(this._userData.id) : 0;
  }


}
